import React, { useState, useEffect } from 'react';
import useTableStore from '../store/useTableStore';
import useSceneStore from '../store/useSceneStore';
import { TOOLS } from '../core/constants';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { v4 as uuidv4 } from 'uuid';

const { FiList, FiPlus, FiTrash2, FiCheck, FiX } = FiIcons;

const TablePanel = () => {
  const { tool, setTool } = useSceneStore();
  const { tables, selectedTableId, isEditingTable, defaultSettings, addTable, updateTable, removeTable, clearTableSelection } = useTableStore();

  const showPanel = tool === TOOLS.TABLE || (selectedTableId && isEditingTable);
  const isEditMode = !!selectedTableId;
  const selectedTable = selectedTableId ? tables[selectedTableId] : null;

  const [config, setConfig] = useState(defaultSettings);

  useEffect(() => {
    if (selectedTable) {
      setConfig({ ...selectedTable, data: JSON.parse(JSON.stringify(selectedTable.data)) });
    } else if (tool === TOOLS.TABLE && !selectedTableId) {
      setConfig(JSON.parse(JSON.stringify(defaultSettings)));
    }
  }, [selectedTableId, tool, defaultSettings]);

  if (!showPanel) return null;

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    if (isEditMode && selectedTableId) {
      updateTable(selectedTableId, { [key]: value });
    }
  };

  const handleDataChange = (id, field, value) => {
    const newData = config.data.map(row => row.id === id ? { ...row, [field]: value } : row);
    handleChange('data', newData);
  };

  const addRow = () => {
    const newRow = { id: uuidv4(), label: 'Nowa cecha', value: '-' };
    handleChange('data', [...config.data, newRow]);
  };

  const removeRow = (id) => {
    handleChange('data', config.data.filter(row => row.id !== id));
  };

  const handleAdd = () => {
    if (!isEditMode) {
      addTable(config);
      setTool(TOOLS.SELECT);
    } else {
      clearTableSelection();
    }
  };

  const handleCancel = () => {
    if (isEditMode) {
      clearTableSelection();
    } else {
      setTool(TOOLS.SELECT);
    }
  };

  const fontSizes = [
    { label: 'Bardzo mały', value: 10 },
    { label: 'Mały', value: 12 },
    { label: 'Normalny', value: 14 },
    { label: 'Duży', value: 18 },
    { label: 'Bardzo duży', value: 24 },
  ];

  return (
    <div className="absolute left-20 top-20 w-96 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden z-20" style={{ maxHeight: 'calc(100vh - 120px)' }}>
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiList} className="text-[#1E2937] text-lg" />
          <h3 className="font-semibold text-gray-700 text-sm">
            {isEditMode ? 'Edycja Metryczki' : 'Nowa Metryczka'}
          </h3>
        </div>
        {isEditMode && (
          <button 
            onClick={() => removeTable(selectedTableId)}
            className="p-1.5 hover:bg-red-100 hover:text-red-600 rounded-lg transition-colors"
            title="Usuń tabelę"
          >
            <SafeIcon icon={FiTrash2} className="text-sm" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <section className="space-y-3">
          <h4 className="text-xs font-bold text-gray-500 uppercase border-b pb-1">Podstawowe ustawienia</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Tytuł tabeli</label>
              <input 
                type="text" 
                value={config.title} 
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2 col-span-2">
              <input 
                type="checkbox" 
                id="showTitle" 
                checked={config.showTitle} 
                onChange={(e) => handleChange('showTitle', e.target.checked)}
                className="rounded border-gray-300 text-[#1E2937] shadow-sm focus:border-gray-300 focus:ring focus:ring-gray-200 focus:ring-opacity-50"
              />
              <label htmlFor="showTitle" className="text-sm text-gray-700">Pokaż tytuł</label>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pozycja X</label>
              <input 
                type="number" 
                value={config.x} 
                onChange={(e) => handleChange('x', parseInt(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pozycja Y</label>
              <input 
                type="number" 
                value={config.y} 
                onChange={(e) => handleChange('y', parseInt(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 mb-1">Szerokość kolumny (px)</label>
              <input 
                type="number" 
                value={config.columnWidth} 
                onChange={(e) => handleChange('columnWidth', parseInt(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h4 className="text-xs font-bold text-gray-500 uppercase border-b pb-1">Formatowanie tekstu</h4>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Rozmiar czcionki</label>
              <div className="flex flex-wrap gap-1">
                {fontSizes.map(fs => (
                  <button 
                    key={fs.value}
                    onClick={() => handleChange('fontSize', fs.value)}
                    className={clsx(
                      "px-2 py-1 text-xs rounded border transition-colors",
                      config.fontSize === fs.value 
                        ? "bg-gray-200 border-[#1E2937] text-[#1E2937]" 
                        : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    {fs.label}
                  </button>
                ))}
              </div>
              <div className="mt-1 text-xs text-gray-400 text-right">{config.fontSize}px</div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Czcionka</label>
              <select 
                value={config.fontFamily} 
                onChange={(e) => handleChange('fontFamily', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              >
                <option value="Inter">Inter (domyślna)</option>
                <option value="Arial">Arial</option>
                <option value="Serif">Serif</option>
                <option value="Monospace">Monospace</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Kolor tekstu</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color" 
                    value={config.color} 
                    onChange={(e) => handleChange('color', e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                  />
                  <span className="text-xs text-gray-500">{config.color}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Tło tabeli</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color" 
                    value={config.backgroundColor} 
                    onChange={(e) => handleChange('backgroundColor', e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                  />
                  <span className="text-xs text-gray-500">{config.backgroundColor}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h4 className="text-xs font-bold text-gray-500 uppercase border-b pb-1">Obramowanie</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="showBorder" 
                checked={config.showBorder} 
                onChange={(e) => handleChange('showBorder', e.target.checked)}
                className="rounded border-gray-300 text-[#1E2937] shadow-sm focus:border-gray-300 focus:ring focus:ring-gray-200 focus:ring-opacity-50"
              />
              <label htmlFor="showBorder" className="text-sm text-gray-700">Pokaż obramowanie</label>
            </div>
            {config.showBorder && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Kolor obramowania</label>
                  <div className="flex items-center gap-2">
                    <input 
                      type="color" 
                      value={config.borderColor} 
                      onChange={(e) => handleChange('borderColor', e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Grubość</label>
                  <input 
                    type="number" 
                    min="1" 
                    value={config.borderWidth} 
                    onChange={(e) => handleChange('borderWidth', parseInt(e.target.value))}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Padding</label>
              <input 
                type="number" 
                value={config.padding} 
                onChange={(e) => handleChange('padding', parseInt(e.target.value))}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex justify-between items-center border-b pb-1">
            <h4 className="text-xs font-bold text-gray-500 uppercase">Dane tabeli</h4>
            <button 
              onClick={addRow}
              className="text-xs text-[#1E2937] font-medium hover:text-black flex items-center gap-1"
            >
              <SafeIcon icon={FiPlus} /> Dodaj wiersz
            </button>
          </div>
          <div className="space-y-2">
            {config.data.map((row, index) => (
              <div key={row.id} className="flex gap-2 items-start group">
                <div className="flex-1">
                  <input 
                    type="text" 
                    value={row.label} 
                    onChange={(e) => handleDataChange(row.id, 'label', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                    placeholder="Nazwa"
                  />
                </div>
                <div className="flex-1">
                  <input 
                    type="text" 
                    value={row.value} 
                    onChange={(e) => handleDataChange(row.id, 'value', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-gray-400 focus:ring-1 focus:ring-gray-400"
                    placeholder="Wartość"
                  />
                </div>
                <button 
                  onClick={() => removeRow(row.id)}
                  className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <SafeIcon icon={FiX} className="text-xs" />
                </button>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex gap-2">
        <button 
          onClick={handleCancel}
          className="flex-1 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          Anuluj
        </button>
        <button 
          onClick={handleAdd}
          className="flex-1 px-3 py-2 bg-[#1E2937] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiCheck} /> {isEditMode ? 'Zapisz zmiany' : 'Dodaj do rzutu'}
        </button>
      </div>
    </div>
  );
};

export default TablePanel;